import { Fragment } from "react";
import { useTranslation } from "@1f/react-sdk";
import VaporTag from "@vapor/react-custom/VaporTag";
import Grid from "@vapor/react-material/Grid";
import Divider from "@vapor/react-material/Divider";
import Stack from "@vapor/react-material/Stack";
import Button from "@vapor/react-material/Button";
import TextField from "@vapor/react-material/TextField";
import Autocomplete from "@vapor/react-material/Autocomplete";
import Checkbox from "@vapor/react-material/Checkbox";
import List from "@vapor/react-material/List";
import ListItem from "@vapor/react-material/ListItem";
import ListItemText from "@vapor/react-material/ListItemText";
import ListItemButton from "@vapor/react-material/ListItemButton";
import ListItemIcon from "@vapor/react-material/ListItemIcon";
import { FormControlLabel, FormGroup, Switch } from "@vapor/react-material";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { it } from "date-fns/locale";
import {
	Print as PrintIcon,
	CheckBox as CheckBoxIcon,
	CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import { Loading } from "../Loading";
import { NoData } from "../NoData";
import { PrestazioniList } from "../prestazione/PrestazioniList";
import { LegaliRappresentantiList } from "./LegaliRappresentantiList";
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import { v4 as uuidv4 } from "uuid";

export const DocumentiTab = ({
	backDropInternalOpen,
	printList,
	selectedPrint,
	filteredUtentiList,
	selectedUsers,
	printDate,
	printCessato,
	printCessPrest,
	prestazioniList,
	utentiList,
	rischiList,
	selectedRischiToPrint,
	filteredSOSList,
	selectedSOSToPrint,
	selectedProfToPrint,
	indAnomaliaList,
	selectedIndAnomaliaToPrint,
	selFascicolo,
	tenantTypeId,
	handlePrintChange,
	handleUserChange,
	handlePrintDate,
	handlePrintCessato,
	handlePrestToPrintClick,
	handleLegalForReport,
	handleRischiToPrintClick,
	handleSOSToPrintClick,
	handlePrintChangeDate,
	handlePrintClick,
	handleIndAnomaliaToPrintClick,
}) => {
	const { t } = useTranslation();

	return (
		<Fragment>
			<Loading open={backDropInternalOpen} />

			<Stack direction="column" spacing={2}>
				<Autocomplete
					id="ddlPrint"
					key={uuidv4()}
					size="small"
					options={printList}
					sx={{ width: "100%" }}
					fullWidth
					onChange={handlePrintChange}
					isOptionEqualToValue={(option, value) =>
						option.id === selectedPrint
					}
					getOptionLabel={(option) =>
						tenantTypeId === 3 ? option.nameAvv : option.name
					}
					defaultValue={
						printList.filter((item) => item.id === selectedPrint)[0]
					}
					renderInput={(params) => (
						<TextField
							{...params}
							required
							label="Seleziona il documento da stampare"
							sx={{ lineHeight: 1 }}
						/>
					)}
				/>

				{selectedPrint !== "" && selectedPrint !== "dicCliente" && (
					<Autocomplete
						id="ddlUtenti"
						key={uuidv4()}
						size="small"
						fullWidth
						multiple
						options={filteredUtentiList}
						sx={{ width: "100%" }}
						getOptionLabel={(option) => option.completeName}
						isOptionEqualToValue={(option, value) =>
							option.userId === value.userId
						}
						defaultValue={selectedUsers}
						renderOption={(props, option, { selected }) => (
							<li {...props}>
								<Checkbox
									id="chbDdlUtenti"
									icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
									checkedIcon={<CheckBoxIcon fontSize="small" />}
									style={{ marginRight: 8 }}
									checked={selected}
								/>
								{option.completeName}
							</li>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								label={`Prestazione ${t("prestazione.form.erogataDa")}`}
								required
								onKeyDown={(event) => {
									event.stopPropagation();
								}}
							/>
						)}
						renderTags={(params) =>
							params.map((item) => (
								<VaporTag
									key={uuidv4()}
									variant="standard"
									type="blueSapphire"
									label={item.completeName}
									sx={{ mr: 1 }}
								/>
							))
						}
						onChange={(event, newValue, reason) => {
							handleUserChange(event, newValue);
							if (event.code !== "Backspace") {
							}
						}}
					/>
				)}

				{selectedPrint !== "" && (
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={it}
					>
						<DatePicker
							label="Data di stampa"
							closeOnSelect
							format="dd/MM/yyyy"
							mask="__/__/____"
							maxDate={new Date()}
							value={isDateValid(printDate) ? new Date(printDate) : null}
							onChange={handlePrintDate}
							slotProps={{
								textField: {
									required: true,
									placeholder: "dd/MM/yyyy",
									size: "small",
									fullWidth: true,
								},
							}}
						/>
					</LocalizationProvider>
				)}

				{selectedPrint !== "" &&
					(selectedPrint === "istruttoria" ||
						selectedPrint === "dicCliente") && (
						<FormGroup row>
							<FormControlLabel
								control={
									<Switch
										id="chkIncludeCessAnag"
										checked={printCessato}
										onChange={handlePrintCessato}
									/>
								}
								label="Includi anagrafiche cessate"
							/>
							<FormControlLabel
								control={
									<Switch
										id="chkIncludeCessPrest"
										checked={printCessPrest}
										onChange={handlePrintCessato}
									/>
								}
								label="Includi prestazioni cessate"
							/>
						</FormGroup>
					)}

				{selectedPrint !== "" &&
				selectedPrint !== "informativa" &&
				selectedPrint !== "dicProf" &&
				selectedPrint !== "valRischi" &&
				selectedPrint !== "indAnomalia" &&
				selectedPrint !== "dicCliente" &&
				selectedUsers.length > 0 ? (
					<Fragment>
						<ExtendedTypography variant="body" color="primary.main">
							{t("nav.prestazioni")} *
						</ExtendedTypography>
						<Divider variant="standard" />
						{prestazioniList.length > 0 ? (
							<PrestazioniList
								prestListFromParent={prestazioniList}
								utentiFromParentList={utentiList}
								onSelection={handlePrestToPrintClick}
								printCessato={printCessPrest}
							/>
						) : (
							<NoData size="small" />
						)}
					</Fragment>
				) : null}

				{selectedPrint !== "" && selectedPrint === "dicCliente" ? (
					<Fragment>
						<ExtendedTypography variant="body" color="primary.main">
							Legali rappresentanti *
						</ExtendedTypography>
						<Divider variant="standard" />
						<LegaliRappresentantiList
							fascicolo={selFascicolo}
							cessato={printCessato}
							onSelection={handleLegalForReport}
						/>
					</Fragment>
				) : null}

				{selectedPrint !== "" && selectedPrint === "valRischi" ? (
					<Fragment>
						<ExtendedTypography variant="body" color="primary.main">
							{t("nav.rischi")} *
						</ExtendedTypography>
						<Divider variant="standard" />
						<List dense disablePadding>
							{rischiList.length > 0 ? (
								rischiList.map((item) => (
									<ListItem key={uuidv4()} disablePadding>
										<ListItemButton
											role={undefined}
											dense
											onClick={() => handleRischiToPrintClick(item)}
										>
											<ListItemIcon>
												<Checkbox
													edge="start"
													checked={
														selectedRischiToPrint !== undefined &&
														selectedRischiToPrint.filter(
															(x) => x.id === item.id,
														).length > 0
													}
													tabIndex={-1}
													disableRipple
												/>
											</ListItemIcon>
											<ListItemText
												key={uuidv4}
												primary={
													"Effettuata in data " +
													item.date +
													" da " +
												(utentiList.filter(
													(x) => x.userId === item.providedBy,
												)[0]?.completeName ?? "")
												}
												secondary={
													item.prestazione.baseTenantTipoId === 3
														? `Totale compessivo A: ${item.sp_a_subTotal}
                                Totale complessivo B: ${item.sp_b_subTotal}
                                Totale : ${item.sp_a_subTotal + item.sp_b_subTotal}`
														: "Rischio inerente:" +
															item.ef_ri_total +
															" | Rischio specifico: " +
															item.ef_sp_total +
															" | Totale: " +
															item.ef_total
												}
											/>
										</ListItemButton>
									</ListItem>
								))
							) : (
								<NoData size="small" />
							)}
						</List>
					</Fragment>
				) : null}

				{selectedPrint === "astensione" && selectedUsers.length > 0 ? (
					<Fragment>
						<ExtendedTypography variant="body" color="primary.main">
							{t("fascicolo.fascicoloAdd.suspiciousTransaction")}
						</ExtendedTypography>
						<Divider variant="standard" />

						<List dense disablePadding>
							{filteredSOSList.length > 0 ? (
								filteredSOSList.map((item) => (
									<ListItem key={uuidv4()} disablePadding>
										<ListItemButton
											role={undefined}
											dense
											onClick={() => handleSOSToPrintClick(item)}
										>
											<ListItemIcon>
												<Checkbox
													edge="start"
													checked={
														selectedSOSToPrint !== null &&
														selectedSOSToPrint.id === item.id
													}
													tabIndex={-1}
													disableRipple
												/>
											</ListItemIcon>
											<ListItemText
												id={uuidv4}
												primary={
													new Date(item.date).toLocaleDateString() +
													" - Prot. N. " +
													item.receiptId
												}
												secondary={
												(utentiList.filter(
													(x) => x.userId === item.reportedByUser,
												)[0]?.completeName ?? "")
												}
											/>
										</ListItemButton>
									</ListItem>
								))
							) : (
								<NoData size="small" />
							)}
						</List>
					</Fragment>
				) : null}

				{selectedPrint === "dicProf" ? (
					<Fragment>
						<ExtendedTypography variant="titleSmall" color="primary.main">
							Informazioni sul professionista attestante
						</ExtendedTypography>
						<Divider variant="standard" />

						<Grid container>
							<Grid item xs={3} sx={{ pr: 2 }}>
								<Stack direction="row" spacing={2}>
									<LocalizationProvider
										dateAdapter={AdapterDateFns}
										adapterLocale={it}
									>
										<DatePicker
											label="Data"
											closeOnSelect
											format="dd/MM/yyyy"
											mask="__/__/____"
											maxDate={new Date()}
											value={
												isDateValid(selectedProfToPrint.date)
													? new Date(selectedProfToPrint.date)
													: null
											}
											onChange={handlePrintChangeDate}
											slotProps={{
												textField: {
													required: true,
													placeholder: "dd/MM/yyyy",
													size: "small",
													fullWidth: true,
												},
											}}
										/>
									</LocalizationProvider>
								</Stack>
							</Grid>
							<Grid item xs={5} sx={{ pr: 2 }}>
								<TextField
									id="txtPrintProfName"
									key={uuidv4()}
									label="Nome e Cognome"
									size="small"
									fullWidth
									required
									defaultValue={selectedProfToPrint.name}
									onChange={handlePrintChange}
									inputProps={{ autocomplete: "nope", maxLength: 500 }}
								/>
							</Grid>
							<Grid item xs={4}>
								<TextField
									id="txtPrintProfAddress"
									key={uuidv4()}
									label="Indirizzo Studio"
									size="small"
									fullWidth
									required
									defaultValue={selectedProfToPrint.address}
									onChange={handlePrintChange}
									inputProps={{ autocomplete: "nope", maxLength: 500 }}
								/>
							</Grid>
						</Grid>
					</Fragment>
				) : null}

				{selectedPrint === "indAnomalia" && selectedUsers.length > 0 ? (
					<Fragment>
						<ExtendedTypography variant="body" color="primary.main">
							{t("fascicolo.fascicoloAdd.indicatoriAnomalia")}
						</ExtendedTypography>
						<Divider variant="standard" />

						<List dense disablePadding>
							{indAnomaliaList.length > 0 ? (
								indAnomaliaList.map((item) => (
									<ListItem key={uuidv4()} disablePadding>
										<ListItemButton
											role={undefined}
											dense
											onClick={() => handleIndAnomaliaToPrintClick(item)}
										>
											<ListItemIcon>
												<Checkbox
													edge="start"
													checked={
														selectedIndAnomaliaToPrint.filter(
															(item2) => item2.id === item.id,
														).length > 0
													}
													tabIndex={-1}
													disableRipple
												/>
											</ListItemIcon>
											<ListItemText
												id={uuidv4}
												primary={
													"Effettuata in data " +
													new Date(item.date).toLocaleDateString() +
													" da " +
												(utentiList.filter(
													(x) => x.userId === item.providedBy,
												)[0]?.completeName ?? "")
												}
												secondary=""
											/>
										</ListItemButton>
									</ListItem>
								))
							) : (
								<NoData size="small" />
							)}
						</List>
					</Fragment>
				) : null}

				<Button
					variant="contained"
					size="small"
					endIcon={<PrintIcon />}
					onClick={handlePrintClick}
				>
					{t("actions.stampa")}
				</Button>
			</Stack>
		</Fragment>
	);
};
